// v77.19 — Parent colour helpers.
//
// Single source of truth on the frontend for "what colour is this parent?".
// The backend persists `parent_color` on every user record and surfaces it
// on every member object in /api/household. We mirror the backend palette
// here as a deterministic fallback so anything rendered before /household
// has loaded (or rendered for a member missing from the local list) still
// gets a stable hue instead of a flicker to "no colour".
//
// Always prefer the stored value via `getParentColor(member)`. Use
// `getParentColorById(userId, members)` when you only have an id.

export const PRIMARY_PARENT_COLOR = "#1F9FA4";

export const COPARENT_COLOR_PALETTE = [
  "#C0392B", // red — sienna brick
  "#7D4FB3", // purple — heather
  "#2E86AB", // blue — denim
  "#D68910", // amber — saffron
  "#5B8A3A", // green — moss
  "#B83280", // magenta — fuchsia
  "#34495E", // slate — deep grey-blue
  "#1ABC9C", // mint — fresh teal-green
];

export const PLACEHOLDER_PARENT_COLOR = "#7A5AA6";

// Stable fallback when a member object has no stored `parent_color`. Uses a
// simple deterministic hash on the user id so the fallback is consistent
// across renders/sessions without needing the backend.
function fallbackFromId(id) {
  if (!id) return COPARENT_COLOR_PALETTE[0];
  let sum = 0;
  for (let i = 0; i < id.length; i += 1) sum += id.charCodeAt(i);
  return COPARENT_COLOR_PALETTE[sum % COPARENT_COLOR_PALETTE.length];
}

export function getParentColor(member) {
  if (!member) return PRIMARY_PARENT_COLOR;
  if (member.parent_color) return member.parent_color;
  // Legacy placeholder field used `color`.
  if (member.color) return member.color;
  if (member.is_placeholder) return PLACEHOLDER_PARENT_COLOR;
  return fallbackFromId(member.id);
}

export function getParentColorById(userId, members) {
  if (!userId) return PRIMARY_PARENT_COLOR;
  const m = (members || []).find((x) => x && x.id === userId);
  if (m) return getParentColor(m);
  return fallbackFromId(userId);
}

// Slightly tinted background (~12% opacity) for chips / cards.
export function tintBg(hex) {
  if (!hex || !hex.startsWith("#") || hex.length !== 7) return "#EEF3F4";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, 0.10)`;
}
